import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  EASY_MODE_STEPS,
  EASY_MODE_STEP_ORDER,
  getNextEasyModeStep,
  getPreviousEasyModeStep
} from '../constants/easyModeSteps';
import { fetchEasyModeCase } from '../services/easyModeService';
import { toaster } from '../utils/toast';
import { getEasyModeStepValidation, resolveEasyModeStep } from '../utils/easyModeValidation';
import useEasyModeContextStep from './useEasyModeContextStep';
import useEasyModeDefendantStep from './useEasyModeDefendantStep';
import useEasyModeDocuments from './useEasyModeDocuments';
import useEasyModeIntake from './useEasyModeIntake';
import useEasyModePartnerImport from './useEasyModePartnerImport';
import useEasyModeSubmit from './useEasyModeSubmit';
import useUserSetting from './useUserSetting';

const STEP_QUERY_PARAM = 'step';

const useEasyModeCase = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [caseData, setCaseData] = useState(null);
  const [isCaseLoading, setIsCaseLoading] = useState(true);
  const hasResolvedStepRef = useRef(false);
  const caseReadRef = useRef(0);
  const [, setPrefersClassicIntake] = useUserSetting({
    key: 'prefersClassicIntake',
    defaultValue: false
  });

  const refetchCase = useCallback(async () => {
    const requestId = caseReadRef.current + 1;
    caseReadRef.current = requestId;

    try {
      const fetchedCase = await fetchEasyModeCase({ caseId });

      // a slower earlier read must not land over a newer one
      if (caseReadRef.current === requestId) setCaseData(fetchedCase);
    } catch (error) {
      // the wizard keeps the case it already has rather than blanking page 1 on a failed read
      console.error('Error fetching easy mode case:', error);
    }
  }, [caseId]);

  const intake = useEasyModeIntake({ caseData });
  const documents = useEasyModeDocuments({ caseId });
  const contextStep = useEasyModeContextStep({ caseData });
  const defendantStep = useEasyModeDefendantStep({ caseData, defendantCount: intake.values.defendantCount });

  const stepValidation = getEasyModeStepValidation({
    intakeValues: intake.values,
    documentRows: documents.rows,
    additionalContext: contextStep.additionalContext
  });

  // Case Intake exists in the database only once it has been saved, and the PFQ rejects a demand whose
  // payload disagrees with the stored case. So every forward exit from step 1 persists it - the stepper
  // and a jump straight to step 3 included, not just Next - or the user is refused a run for a
  // divergence they never caused. A failed save has to stop the move and say so: swallowing it stranded
  // the user on a step that looked finished and was not.
  const persistCaseIntake = async () => {
    try {
      await intake.save();

      return true;
    } catch (error) {
      toaster({
        message: error?.message ?? 'Could not save Case Intake. Check the fields and try again.',
        success: false
      });

      return false;
    }
  };

  const submission = useEasyModeSubmit({
    caseId,
    intakeValues: intake.values,
    additionalContext: contextStep.additionalContext,
    saveIntake: persistCaseIntake,
    saveAdditionalContext: contextStep.save,
    saveDefendant: defendantStep.save,
    stepValidation,
    createRequests: caseData?.createRequests
  });

  // the import writes the case and its documents server-side, so there is nothing to merge here -
  // both halves of the wizard just read back what the partner put there
  const partnerImport = useEasyModePartnerImport({
    caseId,
    onImported: useCallback(
      () => Promise.all([refetchCase(), documents.importRecordsAsRows()]),
      [refetchCase, documents.importRecordsAsRows]
    )
  });

  const requestedStep = Number(searchParams.get(STEP_QUERY_PARAM));

  const step = EASY_MODE_STEP_ORDER.includes(requestedStep)
    ? requestedStep
    : EASY_MODE_STEPS.CASE_INTAKE;

  const setStepInUrl = ({ step: nextStep, replace = false }) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(STEP_QUERY_PARAM, String(nextStep));
    setSearchParams(nextParams, { replace });
  };

  const getIsLeavingCaseIntakeForward = ({ nextStep }) =>
    step === EASY_MODE_STEPS.CASE_INTAKE && nextStep > step;

  const goNext = async () => {
    if (!stepValidation[step].isValid) return;

    const nextStep = getNextEasyModeStep(step);

    if (getIsLeavingCaseIntakeForward({ nextStep }) && !(await persistCaseIntake())) return;

    setStepInUrl({ step: nextStep });
  };

  // backward navigation is always allowed; a jump forward has to clear every step it skips
  const goToStep = async ({ step: nextStep }) => {
    const hasUnfinishedStep = EASY_MODE_STEP_ORDER.filter(
      (candidate) => candidate < nextStep
    ).some((candidate) => !stepValidation[candidate].isValid);

    if (nextStep > step && hasUnfinishedStep) return;

    if (getIsLeavingCaseIntakeForward({ nextStep }) && !(await persistCaseIntake())) return;

    setStepInUrl({ step: nextStep });
  };

  useEffect(() => {
    let isStale = false;
    setIsCaseLoading(true);

    refetchCase().finally(() => {
      if (!isStale) setIsCaseLoading(false);
    });

    return () => {
      isStale = true;
    };
  }, [refetchCase]);

  // the step lives in the URL, but the case and its records decide how far the user may resume, so
  // the clamp waits for both rather than bouncing the user back on first paint
  useEffect(() => {
    if (hasResolvedStepRef.current || isCaseLoading || documents.isRecordsLoading) return;

    hasResolvedStepRef.current = true;
    setStepInUrl({
      step: resolveEasyModeStep({
        caseData,
        recordCount: documents.recordCount,
        requestedStep: searchParams.get(STEP_QUERY_PARAM) ?? EASY_MODE_STEPS.CASE_INTAKE
      }),
      replace: true
    });
  }, [isCaseLoading, documents.isRecordsLoading]);

  const goBack = () => setStepInUrl({ step: getPreviousEasyModeStep(step) });

  const goToClassicIntake = () => {
    setPrefersClassicIntake(true);
    navigate(`/case/edit/${caseId}`);
  };

  return {
    caseId,
    caseData,
    isCaseLoading,
    step,
    stepValidation,
    goToStep,
    goNext,
    goBack,
    goToClassicIntake,
    intake,
    documents,
    contextStep,
    defendantStep,
    partnerImport,
    submission
  };
};

export default useEasyModeCase;
